// Krug 5 (K15.2, docs/KRUG5_K15_xp_i_sezona.md): the season path. Goals with rewards, the same for
// every player, from the first week to the whole season. 30. 9. 2026 (after the first pilot day: an
// active player had 6 of 12 in two days): 20 goals spread over the 49 days, the first ones harder, and
// more kinds of reward (energy, materials for the level, more items). A goal keeps its number `n` (what
// was claimed is kept by number); the list's order is the order on the screen. Progress counts from the
// start of the season; level, Storage and keystone goals are done once the survivor is there. The
// rewards never give XP (the XP curve of K15.1 stays as it is). The server keeps the counters and
// what was claimed (kv), and pays what was done but not claimed when the season rolls over.

import { BUILDINGS } from './content/buildings';
import { DAILY_RULES, stacksLabel } from './content/daily';
import { STACKS } from './content/resources';
import type { BuildingId, Rarity, StackKind, StackMap } from './types';

/**
 * What a goal counts (see the server's `seasonCounts`). 1. 10. 2026 (C2): `building` is a building's level
 * (`building` names it; the old Storage goal is one of them now), `upgrade` the highest +N of an item the
 * survivor has, `trades` deals with Tobias this season, `weeklies` weekly contracts claimed this season.
 * `storage` is the old Storage goal (the sims' list of 30. 9.).
 */
export type SeasonGoalKind = 'expeditions' | 'level' | 'meals' | 'dailies' | 'hordes' | 'storage' | 'keystone' | 'encounters' | 'outfits' | 'building' | 'upgrade' | 'trades' | 'weeklies';

export type SeasonReward =
  | { kind: 'scrap'; qty: number }
  | { kind: 'shards'; qty: number }
  /** An item on the survivor's level (the kind is still called a box in the code). */
  | { kind: 'box'; rarity: Extract<Rarity, 'uncommon' | 'rare' | 'epic'> }
  /** 30. 9. 2026: energy, over the maximum like any reward. */
  | { kind: 'energy'; qty: number }
  /** 30. 9. 2026: building materials worth `days` daily supplies at the survivor's level (SEASON_MATERIALS). */
  | { kind: 'materials'; days: number }
  /** The title `Season N Survivor` and that season's share card frame; both stay for good. */
  | { kind: 'title' };

export interface SeasonGoal {
  /** The goal's number, kept for good (claims are stored by it); the list's order is the screen's. */
  n: number;
  kind: SeasonGoalKind;
  target: number;
  reward: SeasonReward;
  /** The building of a `building` goal. */
  building?: BuildingId;
}

/**
 * The goals of 30. 9. 2026 (20; with A4 of 1. 10.: the outfit goal gives a Rare item, eight hordes an Epic).
 * Simulations only (`useSeasonBefore1Oct`, VARIANT=a and b).
 */
const GOALS_30_9: SeasonGoal[] = [
  { n: 2, kind: 'level', target: 5, reward: { kind: 'box', rarity: 'uncommon' } },
  { n: 8, kind: 'keystone', target: 1, reward: { kind: 'box', rarity: 'rare' } },
  { n: 1, kind: 'expeditions', target: 12, reward: { kind: 'energy', qty: 5 } },
  { n: 3, kind: 'meals', target: 15, reward: { kind: 'shards', qty: 3 } },
  { n: 4, kind: 'dailies', target: 10, reward: { kind: 'materials', days: 2 } },
  { n: 5, kind: 'level', target: 10, reward: { kind: 'shards', qty: 5 } },
  { n: 9, kind: 'encounters', target: 20, reward: { kind: 'box', rarity: 'rare' } },
  { n: 13, kind: 'expeditions', target: 40, reward: { kind: 'energy', qty: 5 } },
  { n: 6, kind: 'hordes', target: 3, reward: { kind: 'box', rarity: 'rare' } },
  { n: 7, kind: 'storage', target: 4, reward: { kind: 'scrap', qty: 150 } },
  { n: 11, kind: 'level', target: 15, reward: { kind: 'box', rarity: 'epic' } },
  { n: 19, kind: 'meals', target: 50, reward: { kind: 'energy', qty: 5 } },
  { n: 10, kind: 'outfits', target: 1, reward: { kind: 'box', rarity: 'rare' } },
  { n: 15, kind: 'dailies', target: 25, reward: { kind: 'box', rarity: 'rare' } },
  { n: 14, kind: 'expeditions', target: 80, reward: { kind: 'materials', days: 3 } },
  { n: 17, kind: 'hordes', target: 8, reward: { kind: 'box', rarity: 'epic' } },
  { n: 20, kind: 'encounters', target: 75, reward: { kind: 'materials', days: 4 } },
  { n: 16, kind: 'dailies', target: 60, reward: { kind: 'shards', qty: 5 } },
  { n: 18, kind: 'level', target: 20, reward: { kind: 'box', rarity: 'epic' } },
  { n: 12, kind: 'expeditions', target: 200, reward: { kind: 'title' } },
];

/**
 * 1. 10. 2026 (C2): the path with the buildings (from the next season the survivor starts over, so the
 * building levels are real goals), an item at +5, deals with Tobias and a weekly contract. In the order the
 * active players get them in `sim:progress 60 4` (the median day of the 24 active players of the two final
 * runs; equal days by n). Of the building
 * goals, the ones the active profiles finished on the same day as another goal (by the profile's median day,
 * in two of the three profiles) were left out: Build the Medical Tent (21), Storage LV 3 (22), Workshop LV 3
 * (24), Barricade LV 3 (30) and Storage LV 4 (7, the goal of 30. 9.; nobody had it on the server). A4: the
 * outfit goal gives a Rare item, eight hordes an Epic one (claims already made stay). Owner decisions after
 * the simulation (1. 10. 2026): 60 expeditions instead of 80 (n 14), no Storage LV 7 (34) or Barricade LV 5
 * (35), Workshop LV 5 gives a Rare item; three Epic items on the path. The rewards never give XP; the Relic
 * Shards stay 13 in all.
 */
/** 23. 10. 2026 (owner, pilot report): the energy of a season goal (5 -> 4, with the other energy rewards). */
export const SEASON_ENERGY = 4;

export const SEASON_GOALS: SeasonGoal[] = [
  { n: 2, kind: 'level', target: 5, reward: { kind: 'box', rarity: 'uncommon' } },
  { n: 8, kind: 'keystone', target: 1, reward: { kind: 'box', rarity: 'rare' } },
  { n: 23, kind: 'trades', target: 5, reward: { kind: 'scrap', qty: 100 } },
  { n: 1, kind: 'expeditions', target: 12, reward: { kind: 'energy', qty: SEASON_ENERGY } },
  { n: 27, kind: 'upgrade', target: 5, reward: { kind: 'materials', days: 2 } },
  { n: 3, kind: 'meals', target: 15, reward: { kind: 'shards', qty: 3 } },
  { n: 25, kind: 'weeklies', target: 1, reward: { kind: 'energy', qty: SEASON_ENERGY } },
  { n: 26, kind: 'building', building: 'barricade', target: 1, reward: { kind: 'materials', days: 1 } },
  { n: 4, kind: 'dailies', target: 10, reward: { kind: 'materials', days: 2 } },
  { n: 5, kind: 'level', target: 10, reward: { kind: 'shards', qty: 5 } },
  { n: 9, kind: 'encounters', target: 20, reward: { kind: 'box', rarity: 'rare' } },
  { n: 13, kind: 'expeditions', target: 40, reward: { kind: 'energy', qty: SEASON_ENERGY } },
  { n: 29, kind: 'building', building: 'medical_tent', target: 3, reward: { kind: 'energy', qty: SEASON_ENERGY } },
  { n: 6, kind: 'hordes', target: 3, reward: { kind: 'box', rarity: 'rare' } },
  { n: 19, kind: 'meals', target: 50, reward: { kind: 'energy', qty: SEASON_ENERGY } },
  { n: 28, kind: 'building', building: 'workshop', target: 4, reward: { kind: 'box', rarity: 'rare' } },
  { n: 11, kind: 'level', target: 15, reward: { kind: 'box', rarity: 'epic' } },
  { n: 15, kind: 'dailies', target: 25, reward: { kind: 'box', rarity: 'rare' } },
  { n: 10, kind: 'outfits', target: 1, reward: { kind: 'box', rarity: 'rare' } },
  { n: 14, kind: 'expeditions', target: 60, reward: { kind: 'materials', days: 3 } },
  { n: 31, kind: 'building', building: 'storage', target: 5, reward: { kind: 'scrap', qty: 250 } },
  { n: 32, kind: 'building', building: 'workshop', target: 5, reward: { kind: 'box', rarity: 'rare' } },
  { n: 33, kind: 'building', building: 'medical_tent', target: 5, reward: { kind: 'materials', days: 3 } },
  { n: 20, kind: 'encounters', target: 75, reward: { kind: 'materials', days: 4 } },
  { n: 17, kind: 'hordes', target: 8, reward: { kind: 'box', rarity: 'epic' } },
  { n: 18, kind: 'level', target: 20, reward: { kind: 'box', rarity: 'epic' } },
  // The last 5 Relic Shards come late (day 42–47), about when the old encounters goal paid them (36–43), so
  // the Relic Forge does not come earlier (K15.2, condition 6).
  { n: 16, kind: 'dailies', target: 60, reward: { kind: 'shards', qty: 5 } },
  { n: 12, kind: 'expeditions', target: 200, reward: { kind: 'title' } },
];

/** VARIANT=a and b (sims only): the path of 30. 9. 2026 instead of the one with the buildings. */
export function useSeasonBefore1Oct() {
  SEASON_GOALS.splice(0, SEASON_GOALS.length, ...GOALS_30_9.map((g) => ({ ...g })));
}

/** Where the counts for a season stand (the server fills it). `keystone` is 1 once one is learned. */
export type SeasonCounts = Record<SeasonGoalKind, number> & {
  /** 1. 10. 2026 (C2): every building's level, for the `building` goals. */
  buildings?: Partial<Record<BuildingId, number>>;
};

/**
 * 6. 10. 2026 (owner): the season that runs now is a test, shown as **Season 0**; the public game starts from Season 1 on a
 * clean database (docs/DEPLOY.md, public launch). Seasons keep their number in the database and the code (1 is the test
 * season: `seasons.number`, Season Score, Legacy, the season states); only the name players see changes. Until the public
 * launch sets `SEASON_NAMES.publicFrom` (the number of the first public season: 1 on the clean database) every season is
 * "Season 0", so a test season after the end of 17. 11. is never taken for the public Season 1. Frame ids (`season_1`)
 * stay as they are.
 */
export const SEASON_NAMES = { publicFrom: null } as { publicFrom: number | null };
/** The number players see for a season: 0 while the game is in its test, then 1, 2… from the first public season. */
export const seasonShown = (season: number) => (SEASON_NAMES.publicFrom == null ? 0 : Math.max(0, season - SEASON_NAMES.publicFrom + 1));
/** "Season 0" for the test season. */
export const seasonName = (season: number) => `Season ${seasonShown(season)}`;

export const seasonTitle = (season: number) => `${seasonName(season)} Survivor`;
/** The share card frame of a season (`collection.frames`, like the set and rank frames). */
export const seasonFrame = (season: number) => `season_${season}`;

export function seasonGoal(n: number): SeasonGoal | undefined {
  return SEASON_GOALS.find((g) => g.n === n);
}

/** Progress toward a goal, never above its target. */
export function seasonProgress(g: SeasonGoal, c: SeasonCounts): number {
  const v = g.kind === 'building' ? (c.buildings?.[g.building ?? 'storage'] ?? 0) : c[g.kind];
  return Math.max(0, Math.min(g.target, Math.floor(v ?? 0)));
}

export const seasonGoalDone = (g: SeasonGoal, c: SeasonCounts) => seasonProgress(g, c) >= g.target;

export function seasonGoalText(g: SeasonGoal): string {
  switch (g.kind) {
    case 'expeditions':
      return `Complete ${g.target} expeditions`;
    case 'level':
      return `Reach level ${g.target}`;
    case 'meals':
      return `Cook ${g.target} meals`;
    case 'dailies':
      return `Claim ${g.target} daily contracts`;
    case 'hordes':
      return `Fight in ${g.target} hordes`;
    case 'storage':
      return `Upgrade Storage to level ${g.target}`;
    case 'keystone':
      return 'Learn a keystone';
    case 'encounters':
      return `Get through ${g.target} encounters with other survivors`;
    case 'outfits':
      return 'Unlock an outfit';
    case 'building': {
      const name = BUILDINGS[g.building ?? 'storage'].name;
      return g.target <= 1 ? `Build the ${name}` : `${name} LV ${g.target}`;
    }
    case 'upgrade':
      return `Upgrade an item to +${g.target}`;
    case 'trades':
      return `Trade with Tobias ${g.target} times`;
    case 'weeklies':
      return g.target <= 1 ? 'Claim a weekly contract' : `Claim ${g.target} weekly contracts`;
  }
}

const RARITY_WORD = { uncommon: 'Uncommon', rare: 'Rare', epic: 'Epic' } as const;

/** Building materials by value (30. 9. 2026): Wood, Metal, Electronics and Chemicals. */
export const SEASON_MATERIALS: [StackKind, number][] = [
  ['wood', 0.3],
  ['metal', 0.3],
  ['electronics', 0.2],
  ['chemicals', 0.2],
];

/** The materials of a `materials` reward at a level: `days` daily supplies' worth. */
export function seasonMaterials(days: number, level: number): StackMap {
  const value = DAILY_RULES.value(level) * days;
  return Object.fromEntries(SEASON_MATERIALS.map(([k, share]) => [k, Math.max(1, Math.round((value * share) / STACKS[k].value))]));
}

/** The reward in words; with the survivor's level, materials say how many. */
export function seasonRewardText(r: SeasonReward, season: number, level?: number): string {
  switch (r.kind) {
    case 'scrap':
      return `${r.qty} Scrap`;
    case 'shards':
      return `${r.qty} Relic Shards`;
    case 'box':
      return `${RARITY_WORD[r.rarity]} item`;
    case 'energy':
      return `+${r.qty} energy`;
    case 'materials':
      return level ? stacksLabel(seasonMaterials(r.days, level)) : 'Materials for your level';
    case 'title':
      return `Title ${seasonTitle(season)} and the season's card frame`;
  }
}

/** Relic Shards the whole path gives (K15.2: 13). */
export const SEASON_SHARDS = SEASON_GOALS.reduce((a, g) => a + (g.reward.kind === 'shards' ? g.reward.qty : 0), 0);
